import React from 'react';
import { logReactError } from '@/utils/debugLogger';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Error Boundary — ловит ошибки React компонентов
 *
 * При ошибке показывает экран с деталями и кнопкой перезагрузки.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Логируем в debug panel
    logReactError(error, errorInfo?.componentStack);

    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV !== 'production';

      const errorMessage = isDev
        ? (this.state.error?.message || 'Unknown error')
        : 'Произошла ошибка. Обратитесь к администратору.';

      const helpText = isDev
        ? 'Компонент упал с ошибкой. Информация ниже поможет в отладке.'
        : 'Попробуйте перезагрузить страницу.';

      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Произошла ошибка
                </h1>
                <p className="text-gray-600">{helpText}</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <p className="font-mono text-sm text-red-800">{errorMessage}</p>
            </div>

            {isDev && this.state.error?.stack && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">
                  Stack trace
                </summary>
                <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-auto max-h-48 font-mono text-gray-700">
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            {isDev && this.state.errorInfo?.componentStack && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">
                  Component stack
                </summary>
                <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-auto max-h-48 font-mono text-gray-700">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3">
              <Button onClick={this.handleReload} className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Перезагрузить страницу
              </Button>
              <Button variant="outline" onClick={this.handleReset}>
                Попробовать снова
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
